'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@eversports/klimt-design-components/tailwind/utils';
import Button from '@eversports/klimt-primitives/Button';
import { useParams, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useTransition } from 'react';
import type { Locale } from '../../../../../../localization/i18n';
import { CLASSNAME_CONTAINED_CONTENT } from '../../../../../App.constants';
import getGraphqlForClient from '../../../../../lib/get-graphql-for-client';
import usePaginatedData from '../../../../../lib/use-paginated-data';
import ActivityDateGroup from '../components/ActivityDateGroup';
import { EVENT_LIMIT, EVENT_TYPES } from '../Discover.constants';
import formatQueryParams from '../helpers/format-query-params';
import groupActivitiesByDate from '../helpers/group-activities-by-date';
import type { EventBookableItem } from './ActivityCard/ActivityCard.types';
import Filters from './Filters';
import { FILTER_KEY_URL_PARAMS } from './Filters/Filters.types';
interface Props {
  initialActivityData: Array<EventBookableItem>;
  initialPageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
}
export default function ActivityResults(t0) {
  const $ = _c(56);
  const {
    initialActivityData,
    initialPageInfo
  } = t0;
  const t = useTranslations("discover-sport-city");
  const [isPending, startTransition] = useTransition();
  const {
    city,
    sport,
    locale
  } = useParams();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== city || $[1] !== searchParams || $[2] !== sport) {
    t1 = async t2 => {
      const {
        afterCursor
      } = t2;
      const eventFiltersFromUrlParams = formatQueryParams({
        dateParam: searchParams.get(FILTER_KEY_URL_PARAMS.DATE),
        timeParam: searchParams.get(FILTER_KEY_URL_PARAMS.START_TIME),
        activityLocationParam: searchParams.getAll(FILTER_KEY_URL_PARAMS.ACTIVITY_LOCATION),
        levelParam: searchParams.getAll(FILTER_KEY_URL_PARAMS.LEVEL)
      });
      const response = await getGraphqlForClient().DiscoverActivitiesDiscoverContextByCityAndSportSlug({
        citySlug: city,
        sportSlug: sport,
        first: EVENT_LIMIT,
        after: afterCursor,
        eventTypes: EVENT_TYPES,
        ...eventFiltersFromUrlParams
      });
      const activityData = response.discoverContextByCityAndSportSlug.eventBookableItems.nodes.filter(_temp);
      const pageInfo = response.discoverContextByCityAndSportSlug.eventBookableItems.pageInfo;
      return {
        data: activityData,
        pageInfo
      };
    };
    $[0] = city;
    $[1] = searchParams;
    $[2] = sport;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const activityDataQuery = t1;
  let t2;
  if ($[4] !== activityDataQuery || $[5] !== initialActivityData || $[6] !== initialPageInfo) {
    t2 = {
      initialData: initialActivityData,
      initialPageInfo,
      query: activityDataQuery
    };
    $[4] = activityDataQuery;
    $[5] = initialActivityData;
    $[6] = initialPageInfo;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: activityData_0,
    pageInfo: pageInfo_0,
    isLoading,
    handleLoadMore
  } = usePaginatedData(t2);
  if (!activityData_0.length) {
    let t3;
    if ($[8] !== locale) {
      t3 = <Filters startTransition={startTransition} locale={locale} />;
      $[8] = locale;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = cn(CLASSNAME_CONTAINED_CONTENT, "flex h-10 items-center justify-center font-medium");
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t) {
      t5 = t("no-results-headers");
      $[11] = t;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t5) {
      t6 = <div className={t4}>{t5}</div>;
      $[13] = t5;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== isPending) {
      t7 = isPending && <div role="status" className="absolute top-[90px] left-1/2 z-10 -translate-x-1/2"><div className="size-6 animate-spin rounded-full border-4 border-solid border-border-brand-primary border-t-transparent" /></div>;
      $[15] = isPending;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    let t8;
    if ($[17] !== t6 || $[18] !== t7) {
      t8 = <div className="relative h-20 bg-surface-primary py-3">{t6}{t7}</div>;
      $[17] = t6;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== t3 || $[21] !== t8) {
      t9 = <>{t3}{t8}</>;
      $[20] = t3;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[23] !== activityData_0 || $[24] !== isPending || $[25] !== locale) {
    const groupedActivities = groupActivitiesByDate(activityData_0);
    if ($[31] !== locale) {
      t7 = <Filters startTransition={startTransition} locale={locale} />;
      $[31] = locale;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    t5 = "relative border-t border-border-divider bg-surface-primary py-2";
    if ($[33] !== isPending) {
      t6 = isPending && <div role="status" className="absolute top-4 left-1/2 z-10 -translate-x-1/2"><div className="size-6 animate-spin rounded-full border-4 border-solid border-border-brand-primary border-t-transparent" /></div>;
      $[33] = isPending;
      $[34] = t6;
    } else {
      t6 = $[34];
    }
    const t8 = isPending && "opacity-50";
    if ($[35] !== t8) {
      t3 = cn(CLASSNAME_CONTAINED_CONTENT, t8, "flex flex-col items-center");
      $[35] = t8;
      $[36] = t3;
    } else {
      t3 = $[36];
    }
    let t9;
    if ($[37] !== locale) {
      t9 = (group, index) => <ActivityDateGroup key={group.groupDate} groupDate={group.groupDate} locale={locale} activities={group.activities} hasPriority={index === 0} />;
      $[37] = locale;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    t4 = groupedActivities.map(t9);
    $[23] = activityData_0;
    $[24] = isPending;
    $[25] = locale;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t3 = $[26];
    t4 = $[27];
    t5 = $[28];
    t6 = $[29];
    t7 = $[30];
  }
  let t8;
  if ($[39] !== handleLoadMore || $[40] !== isLoading || $[41] !== isPending || $[42] !== pageInfo_0.hasNextPage || $[43] !== t) {
    t8 = pageInfo_0.hasNextPage && <Button disabled={isPending || isLoading} onClick={handleLoadMore} className="mb-4 w-24">{isLoading ? <div role="status"><div className="size-2 animate-spin rounded-full border border-solid border-white border-t-transparent" /></div> : t("load-more")}</Button>;
    $[39] = handleLoadMore;
    $[40] = isLoading;
    $[41] = isPending;
    $[42] = pageInfo_0.hasNextPage;
    $[43] = t;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  let t9;
  if ($[45] !== t3 || $[46] !== t4 || $[47] !== t8) {
    t9 = <div className={t3}>{t4}{t8}</div>;
    $[45] = t3;
    $[46] = t4;
    $[47] = t8;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== t5 || $[50] !== t6 || $[51] !== t9) {
    t10 = <div className={t5}>{t6}{t9}</div>;
    $[49] = t5;
    $[50] = t6;
    $[51] = t9;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  let t11;
  if ($[53] !== t10 || $[54] !== t7) {
    t11 = <>{t7}{t10}</>;
    $[53] = t10;
    $[54] = t7;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  return t11;
}
function _temp(eventBookableItem) {
  return eventBookableItem.eventSessions?.[0];
}